import { api } from '@/api/endpoints';
import { ackQueueService } from '@/services/ackQueue';
import { connectivity } from '@/services/connectivity';
import { logQueueService } from '@/services/logQueue';
import { mediaQueueService } from '@/services/mediaQueue';
import { getDb } from '@/data/db';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { StorageKeys, SyncIntervals } from '@/lib/config';
import { createLogger } from '@/lib/logger';
import type { AssignmentDtoApi } from '@/types/domain';
import { mapAssignmentDto } from '@/types/domain';

const log = createLogger('sync');

export type SyncContext = 'login' | 'manual' | 'mainMenu' | 'loadUnload' | 'background' | 'visibility';

interface SyncListenerArgs {
  context: SyncContext;
  status: 'started' | 'success' | 'failure';
  error?: unknown;
  newCount?: number;
  modifiedCount?: number;
}

type SyncListener = (args: SyncListenerArgs) => void;

class SyncService {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private currentContext: SyncContext = 'mainMenu';
  private emptyPolls = 0;
  private inflight: Promise<void> | null = null;
  private listeners = new Set<SyncListener>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.runOnce('visibility').catch(() => undefined);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.runOnce('visibility').catch(() => undefined);
        }
      });
    }
  }

  /**
   * Set the active context. Each context owns its own polling cadence.
   */
  setContext(context: SyncContext): void {
    if (this.currentContext === context) return;
    this.currentContext = context;
    this.emptyPolls = 0;
    this.scheduleNext();
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(args: SyncListenerArgs): void {
    for (const l of this.listeners) {
      try {
        l(args);
      } catch {
        /* ignore */
      }
    }
  }

  private intervalForContext(context: SyncContext): number {
    switch (context) {
      case 'loadUnload':
        return SyncIntervals.loadUnload;
      case 'mainMenu':
        return SyncIntervals.mainMenu;
      case 'background':
        return SyncIntervals.background;
      default:
        return SyncIntervals.mainMenu;
    }
  }

  private scheduleNext(): void {
    if (this.timer) clearTimeout(this.timer);
    const base = this.intervalForContext(this.currentContext);
    const factor = Math.min(2 ** this.emptyPolls, 4);
    const delay = Math.min(base * factor, SyncIntervals.emptyPollBackoffMax);
    this.timer = setTimeout(() => {
      this.runOnce(this.currentContext).catch((err) => log.warn('scheduled sync failed', err));
    }, delay);
  }

  /**
   * Run a single sync cycle: pull assignments, push log queue, push media queue, ack new.
   */
  async runOnce(context: SyncContext): Promise<void> {
    if (this.inflight) return this.inflight;
    if (!connectivity.online) {
      log.debug('skipping sync: offline');
      this.scheduleNext();
      return;
    }
    this.emit({ context, status: 'started' });
    this.inflight = (async () => {
      try {
        const lastSync = localStorage.getItem(StorageKeys.lastSync) ?? undefined;
        const fullLoad = !lastSync;
        const result = await api.syncAssignments(lastSync);
        const newOnes = ((result.newAssignments ?? []) as AssignmentDtoApi[]).map(mapAssignmentDto);
        const modified = ((result.modifiedAssignments ?? []) as AssignmentDtoApi[]).map(
          mapAssignmentDto,
        );

        await getDb().upsertAssignmentsPreservingLocal(
          newOnes.map((a) => ({ ...a, receivedAt: new Date().toISOString() })),
        );
        await getDb().upsertAssignmentsPreservingLocal(modified);

        // Reassigned-away calls (server flipped rem_f31_switch to 75): delete
        // the local copies so the call disappears from this device. TMS makes
        // a NEW record for whichever driver actually gets the call.
        const removedIds = (result.removedAssignmentIds ?? []) as string[];
        if (removedIds.length > 0) {
          const removed = await assignmentRepo.removeAssignments(removedIds);
          if (removed > 0) {
            log.info(`removed ${removed} reassigned assignment(s) from device`);
          }
        }

        // A full load (no lastSync) returns the driver's COMPLETE set of open
        // calls, so it also reconciles: local records the server no longer
        // lists (archived at 99999, missed 75s, flood leftovers) are pruned.
        // This makes sign-out/sign-in a true device reset.
        if (fullLoad) {
          const openIds = [...newOnes, ...modified].map((a) => a.remF04Id);
          const pruned = await assignmentRepo.pruneToOpenSet(openIds);
          if (pruned > 0) {
            log.info(`full load pruned ${pruned} stale local assignment(s)`);
          }
        }

        if (newOnes.length > 0) {
          await ackQueueService.enqueue(newOnes.map((a) => a.remF04Id));
        }

        localStorage.setItem(StorageKeys.lastSync, result.serverTimestamp);

        const empty = newOnes.length === 0 && modified.length === 0;
        this.emptyPolls = empty ? this.emptyPolls + 1 : 0;

        // Daily automatic recovery of quarantined items (internally throttled)
        // runs before the flushes so re-queued entries upload this cycle.
        const { quarantineRetryService } = await import('@/services/quarantineRetry');
        await quarantineRetryService.maybeRun();

        await logQueueService.flush();
        await mediaQueueService.flush();
        await ackQueueService.flush();
        // End-to-end POD acknowledgment + retention (Phase 2).
        await mediaQueueService.verifyPods();
        await mediaQueueService.purgeExpired();

        // Device-status heartbeat for the admin fleet board (fire and forget,
        // internally throttled; must never affect the sync cycle).
        const { heartbeatService } = await import('@/services/heartbeat');
        void heartbeatService.report();

        this.emit({
          context,
          status: 'success',
          newCount: newOnes.length,
          modifiedCount: modified.length,
        });
      } catch (err) {
        log.warn('sync failed', err);
        this.emit({ context, status: 'failure', error: err });
      } finally {
        this.inflight = null;
        this.scheduleNext();
      }
    })();
    return this.inflight;
  }

  /**
   * Stop the timer (e.g. on logout). Clears all queued work.
   */
  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.inflight = null;
  }
}

export const syncService = new SyncService();
